import React, { useCallback, useEffect, useState } from 'react'
import {
  track,
  identify,
  trackPage,
  resetSegmentIdentity,
  trackExperimentViewed,
  trackExperimentBucketed,
  adPartnersTo,
} from '@mc/track-event'
import { SegmentTrackingContext, TrackOptions } from '@mc/tracking-context'
import { useUser } from '../contexts/AuthContext'

const defaultIntegrations = adPartnersTo(false)

const AUTH_WAIT_TIMEOUT_MS = 5000

const createDeferred = () => {
  let resolve: () => void = () => {}
  const promise = new Promise<void>((res) => {
    resolve = res
  })
  return { promise, resolve }
}

export const SegmentTrackingProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { user, isLoading } = useUser()
  const [authResolved] = useState(createDeferred)

  useEffect(() => {
    if (!isLoading && user?.analytics) {
      // Send the server-authoritative educator role (`learner`/`beta`/`preview`)
      // as a user-level identify trait so analytics can segment by role
      // (JAR-2378). This replaces the former `is_beta` boolean. Role is a
      // user-level attribute only — it is never tagged onto individual events.
      identify(user.analytics.userId, {
        email: user.email,
        user_id: user.analytics.userId,
        educator_role: user.role,
      })
    }
  }, [user, isLoading])

  const wrappedTrack = useCallback(
    (
      event: string,
      properties?: Record<
        string,
        string | number | boolean | object | null | undefined
      >,
      options?: TrackOptions,
    ) =>
      track(
        event,
        { product: 'educator', ...(properties ?? {}) },
        {
          integrations: options?.integrations ?? defaultIntegrations,
        },
      ),
    [],
  )

  useEffect(() => {
    if (!isLoading) {
      authResolved.resolve()
    }
  }, [authResolved, isLoading])

  // Never hold page views hostage to a stalled or abandoned session check
  useEffect(() => {
    const timeoutId = setTimeout(authResolved.resolve, AUTH_WAIT_TIMEOUT_MS)
    return () => clearTimeout(timeoutId)
  }, [authResolved])

  const wrappedTrackPage = useCallback(
    async (
      event?: string,
      properties?: Record<string, unknown>,
      options?: TrackOptions,
    ) => {
      // Don't drop this wait: until AuthContext primes the account cache, trackPage fires the jsonapi lookups
      await authResolved.promise
      return trackPage(
        event,
        {
          platform: 'web',
          product: 'educator',
          ...properties,
        },
        { integrations: options?.integrations ?? defaultIntegrations },
      )
    },
    [authResolved],
  )

  const wrappedTrackExperimentViewed = (
    experimentName: string,
    properties: Record<string, unknown> = {},
    options: { trackUserLeap?: boolean } = {},
  ) =>
    trackExperimentViewed(
      experimentName,
      { product: 'educator', ...properties },
      options,
    )

  return (
    <SegmentTrackingContext.Provider
      value={{
        identify,
        track: wrappedTrack,
        trackPage: wrappedTrackPage,
        resetSegmentIdentity,
        trackExperimentViewed: wrappedTrackExperimentViewed,
        trackExperimentBucketed,
      }}
    >
      {children}
    </SegmentTrackingContext.Provider>
  )
}
